<template>
  <div class="relative p-0 bg-transparent border-0 group" @focusin="focusInput">
    <Listbox v-model="selected_item" as="div" :class="classes">
      <ListboxLabel
        v-if="label"
        :class="[
          isInputFocused || search_term ? ' opacity-0 -top-0.5 text-sm' : 'top-4 text-base',
          'z-0',
          'bg-transparent',
          isInputFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
        ]"
        class="absolute z-20 flex items-center h-5 p-0 transition-all duration-200 ease-in-out transform -translate-y-1/2 rounded-full pointer-events-none select-none text-common-grey left-10"
        @focusin="focusInput">
        {{ label }}

        <div
          v-if="tooltipInfo"
          class="p-0.5 relative z-30 w-4 h-4 bg-[#899399] flex items-center justify-center rounded-full text-white ml-2 mt-0.5 has-tooltip">
          ?
          <span
            class="inline-flex ml-6 mt-14 text-common-grey items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
            {{ tooltipInfo }}</span>
        </div>
      </ListboxLabel>
      <div v-if="withTags">
        <span v-for="(t, i) in selectedItems" :key="i" :style="{ background: 'grey', borderRadius: '30px', padding: '2px 8px' }">
          <span :style="{ fontSize: '12px' }">
            {{ t }}
          </span>
          <button
            type="button"
            class="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-dark focus:outline-none focus:bg-blue-light"
            @click="removeItem(t)">
            <span class="sr-only">{{ $t("Remove large option") }}</span>
            <svg class="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
              <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
            </svg>
          </button>
        </span>
      </div>

      <ListboxButton as="template">
        <div class="flex rounded-lg">
          <div class="relative flex items-stretch flex-grow">
            <div v-if="icon" class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <component :is="icon" class="w-5 h-5 text-common-grey" aria-hidden="true" />
            </div>

            <div v-if="showDownIcon" class="absolute !cursor-pointer inset-y-0 right-2 pl-3 flex items-center pointer-events-none">
              <ChevronDown class="w-5 h-5 text-common-grey" aria-hidden="true" />
            </div>

            <input
              ref="inputField"
              v-model="search_term"
              :disabled="disabled || inputDisabled"
              autocomplete="off"
              class="form-input !focus:border-transparent !focus:outline-none"
              :placeholder="placeholder"
              type="text"
              :class="[
                icon ? 'pl-10 py-[7px]' : '',
                search_term?.length > 0 && !disabled && showClearBtn ? 'rounded-l-lg' : 'rounded-lg',
                inputClasses,
                { 'bg-gray-100': isBoxBackground }
              ]"
              @click="showAllItems()"
              @keyup="
                ($event) => {
                  $emit('changed', search_term)
                }
              "
              @keydown="
                ($event) => {
                  if ($event.keyCode == 32) {
                    $event.stopPropagation()
                  }
                  inputChange()
                }
              "
              @input="
                ($event) => {
                  if ($event.keyCode != 38 && $event.keyCode != 40) {
                    show_results = true
                  }
                }
              ">
          </div>
          <button
            v-show="search_term?.length > 0 && !disabled && showClearBtn"
            type="button"
            :class="{ 'right-6 mt-0.5': showDownIcon }"
            class="absolute inline-flex items-center px-1 py-2 text-sm font-normal transition duration-200 ease-in-out right-2 dark:bg-dark-screen-background group dark:border-dark-grey-text focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue"
            @click="reset">
            <X class="w-5 h-5 text-common-grey group-hover:text-gray-700 dark:group-hover:text-gray-200" aria-hidden="true" />
          </button>
        </div>
      </ListboxButton>

      <transition
        v-show="search_results.length > 0 && show_results"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0">
        <ListboxOptions
          static
          :class="isBoxBackground ? ' z-40 bg-gray-100 dark:bg-dark-box-background' : 'z-40 bg-white dark:bg-dark-box-background'"
          class="absolute z-30 w-full mt-1 overflow-auto text-base transition duration-200 ease-in-out rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          <ListboxOption v-for="item in search_results" v-slot="{ active }" :key="item" :value="item">
            <div
              :class="{
                'bg-light-brand-blue dark:bg-dark-brand-blue text-white': active
              }"
              class="relative py-2 pl-8 pr-4 transition duration-100 ease-in-out rounded-lg cursor-pointer group hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue hover:text-dark-body-text text-light-body-text dark:text-dark-body-text">
              <span class="block truncate">
                {{ is_data_string_list ? item : labelAttr ? item[labelAttr] : item.name }}
              </span>
              <span
                v-if="showPlusBtn"
                class="absolute inset-y-0 left-0 flex items-center pl-1.5 hover:text-dark-body-text text-light-body-text dark:text-dark-body-text transition ease-in-out duration-100">
                <Plus class="w-5 h-5" aria-hidden="true" />
              </span>
            </div>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </Listbox>
  </div>
</template>

<script>
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, ListboxLabel } from "@headlessui/vue"
import { ChevronDown, Search, X, Plus } from "lucide-vue-next"
import dataService from "@/services/data.service"

export default {
  components: {
    Search,
    ChevronDown,
    X,
    Plus,
    Listbox,
    ListboxOptions,
    ListboxOption,
    ListboxButton,
    ListboxLabel
  },
  props: {
    tooltipInfo: null,
    valueAttr: null,
    showDownIcon: null,
    value: {
      type: String,
      required: false
    },
    placeholder: {
      type: String,
      required: false
    },
    inputClasses: {
      type: String,
      default: ""
    },
    classes: {
      type: String,
      default: ""
    },
    data: {
      default: [],
      required: false
    },
    inputDisabled: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showClearBtn: {
      type: Boolean,
      default: true
    },
    showPlusBtn: {
      type: Boolean,
      default: true
    },
    isAsync: {
      type: Boolean,
      default: false
    },
    endpointAsyncUrl: {
      type: String,
      default: null
    },
    objectType: {
      type: String,
      default: null
    },
    staticSuggestions: {
      type: Array,
      default: () => []
    },
    asyncEndpointUrlParam: {
      type: String,
      default: "query"
    },
    separator: {
      type: String,
      default: ","
    },
    // Only applies when data is lists of objects
    clearOnSelect: {
      type: Boolean,
      default: true
    },
    emitSelectedObject: {
      type: Boolean,
      default: true
    },
    labelAttr: {
      type: String
    },
    icon: {
      type: [Object, Function], // Allow both object and function components
      default: () => Search
    },
    withTags: {
      type: Boolean,
      default: false
    },
    endpointMethod: {
      type: String,
      default: "get"
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      required: false
    },
    ownersFilter: {
      type: Array,
      default: null
    },
    autoFocus: null
  },
  emits: ["chosen", "changed", "selectedObject", "reset"],

  data() {
    return {
      endpoint_async_url: null,
      show_results: false,
      search_term: "",
      search_results: [],
      selected_item: null,
      selectedItems: [],
      isInputFocused: false
    }
  },
  computed: {
    is_data_string_list() {
      if (this.isAsync) {
        return true
      } else {
        if (this.data.length > 0) {
          return typeof this.data[0] == "string"
        }
      }
      return false
    }
  },
  watch: {
    value() {
      if (this.labelAttr && this.value) {
        this.search_term = this.data.find((item) => (this.valueAttr ? item[this.valueAttr] : item.value) == this.value)
          ? this.data.find((item) => (this.valueAttr ? item[this.valueAttr] : item.value) == this.value)[this.labelAttr]
          : null
      } else {
        this.search_term = this.value
      }
    },
    data() {
      if (this.labelAttr && this.value) {
        this.search_term = this.data.find((item) => (this.valueAttr ? item[this.valueAttr] : item.value) == this.value)
          ? this.data.find((item) => (this.valueAttr ? item[this.valueAttr] : item.value) == this.value)[this.labelAttr]
          : null
      } else {
        this.search_term = this.value
      }
    },
    search_term() {
      if (this.isAsync) {
        this.$utils.debounce(() => {
          this.getAsyncResults()
        }, 400)
      } else {
        if (this.search_term != null) {
          const search_term_clean = this.search_term.includes(this.separator)
            ? this.search_term.split(this.separator)[this.search_term.split(this.separator).length - 1]
            : this.search_term
          if (this.is_data_string_list) {
            this.search_results = this.$utils.filterAndSort(this.data.concat(this.staticSuggestions), search_term_clean, null)
          } else {
            let search_fields = this.labelAttr ? [this.labelAttr] : ["name"]
            this.search_results = this.$utils.filterAndSort(this.data.concat(this.staticSuggestions), search_term_clean, search_fields)
          }
        } else {
          this.show_results = false
        }
      }
    },

    selected_item() {
      if (this.selected_item) {
        this.show_results = false
        this.search_results = []

        if (!this.is_data_string_list) {
          this.$emit("chosen", { ...this.selected_item })
        } else {
          if (!this.withTags) {
            this.$emit("chosen", this.selected_item)
            this.search_term = this.selected_item
          } else {
            this.selectedItems.push(this.selected_item)
            const val = this.selectedItems.join(this.separator)
            this.$emit("chosen", val)
            if (this.emitSelectedObject) {
              this.$emit("selectedObject", this.selected_item)
            }

            this.search_term = ""
          }
        }
        if (!this.is_data_string_list) {
          if (this.clearOnSelect) {
            this.search_term = null
          } else {
            if (this.labelAttr) {
              this.search_term = this.selected_item[this.labelAttr]
            } else {
              // set the input value to the first item key value
              this.search_term = this.selected_item[Object.keys(this.selected_item)[0]]
            }
          }
        }
        this.selected_item = null
      }
    }
  },
  created() {
    if (this.labelAttr && this.value) {
      this.search_term = this.search_term = this.data.find((item) => (this.valueAttr ? item[this.valueAttr] : item.value) == this.value)
        ? this.data.find((item) => (this.valueAttr ? item[this.valueAttr] : item.value) == this.value)[this.labelAttr]
        : null
    } else {
      this.search_term = this.value
    }
    if (this.objectType) {
      this.endpoint_async_url = this.getObjectTypeMetricNameSuggestionUrl()
    }
    document.addEventListener("mousedown", () => {
      this.show_results = false
    })

    if (this.autoFocus) {
      setTimeout(() => {
        this.focusInput()
        this.showAllItems()
      }, 200)
    }
  },

  methods: {
    getObjectTypeMetricNameSuggestionUrl() {
      return `/api/organizations/${this.$store.state.organization.id}/metrics/${this.$utils.getOptionMetricReference(this.objectType, "name", "id")}/suggestions/`
    },
    showAllItems() {
      this.search_results = this.data
      this.show_results = true

      this.focusInput()
    },
    focusInput() {
      this.isInputFocused = true

      if (!this.selected_item && this.staticSuggestions.length) {
        this.show_results = true
        this.search_results = this.staticSuggestions.filter((item) => {
          const search_term_clean = this.search_term.includes(this.separator)
            ? this.search_term.split(this.separator)[this.search_term.split(this.separator).length - 1]
            : this.search_term
          return item.toLowerCase().includes(search_term_clean.toLowerCase())
        })
      }
      this.$refs.inputField.focus()
    },

    getAsyncResults() {
      if (this.search_term != null) {
        const search_term_clean = this.search_term.includes(this.separator)
          ? this.search_term.split(this.separator)[this.search_term.split(this.separator).length - 1]
          : this.search_term

        let url = this.endpointAsyncUrl
        if (this.objectType) {
          url = this.getObjectTypeMetricNameSuggestionUrl()
        } else if (typeof url !== "string") {
          url = this.endpointAsyncUrl()
        }

        let filters = {}

        filters[this.asyncEndpointUrlParam] = search_term_clean
        if (this.selectedItems.length > 1) {
          const query = this.selectedItems.concat(search_term_clean).join(this.separator)
          filters[this.asyncEndpointUrlParam] = query
        }
        filters["owners"] = this.ownersFilter
        dataService.callEndpointByUrl(url, filters, this.endpointMethod).then((res) => {
          const search_results_from_url = res.data
          const search_results_from_static = this.staticSuggestions.filter((item) => {
            const search_term_clean = this.search_term.includes(this.separator)
              ? this.search_term.split(this.separator)[this.search_term.split(this.separator).length - 1]
              : this.search_term
            return item.toLowerCase().includes(search_term_clean.toLowerCase())
          })
          this.search_results = search_results_from_url.concat(search_results_from_static)
        })
      } else {
        this.show_results = false
      }
    },
    reset() {
      this.search_term = ""
      this.search_results = []
      this.show_results = false
      this.$emit("reset")
    },

    inputChange() {
      if (this.selectedItems.length > 1) {
        const val = this.selectedItems.join(this.separator)
        this.$emit("changed", `${val},${this.search_term}`)
      }
      this.$emit("changed", this.search_term)
    },
    removeItem(tag) {
      this.selectedItems = this.selectedItems.filter((item) => item !== tag)
      const val = this.selectedItems.join(this.separator)
      this.$emit("chosen", val)
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
